import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";

const SERVICE_OPTS = ["Detergent products", "ICT Consultancy", "Software Development", "Other"];

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", service_interest: "" });
    const [loading, setLoading] = useState(false);

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    async function submit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contact", form);
            toast.success("Message sent! We'll reply within 1 business day.");
            setForm({ name: "", email: "", phone: "", subject: "", message: "", service_interest: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not send message");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div data-testid="contact-page">
            <section className="bg-ink text-white py-24 md:py-28">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    <div className="overline text-primary mb-4">Contact</div>
                    <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1] tracking-tight">
                        Let's talk.
                    </h1>
                    <p className="text-lg text-white/70 mt-6 max-w-2xl">
                        Got a question, a quote request, or want to partner? Reach out via the form or one of the channels below.
                    </p>
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 grid md:grid-cols-[1.4fr_1fr] gap-12">
                <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                    <h2 className="font-display text-2xl font-bold mb-4">Send us a message</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <input required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="border border-border px-3 py-3 focus:outline-none focus:border-primary" data-testid="contact-name" />
                        <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" className="border border-border px-3 py-3 focus:outline-none focus:border-primary" data-testid="contact-email" />
                        <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone (optional)" className="border border-border px-3 py-3 focus:outline-none focus:border-primary" data-testid="contact-phone" />
                        <select value={form.service_interest} onChange={(e) => set("service_interest", e.target.value)} className="border border-border px-3 py-3 focus:outline-none focus:border-primary bg-white" data-testid="contact-service">
                            <option value="">I'm interested in…</option>
                            {SERVICE_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <input required value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Subject" className="w-full border border-border px-3 py-3 focus:outline-none focus:border-primary" data-testid="contact-subject" />
                    <textarea required value={form.message} onChange={(e) => set("message", e.target.value)} rows={6} placeholder="How can we help?" className="w-full border border-border px-3 py-3 focus:outline-none focus:border-primary" data-testid="contact-message" />
                    <button type="submit" disabled={loading} className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 font-semibold hover:bg-primary-hover disabled:opacity-50" data-testid="contact-submit">
                        <Send className="w-4 h-4" /> {loading ? "Sending…" : "Send message"}
                    </button>
                </form>

                <div className="space-y-6">
                    <h2 className="font-display text-2xl font-bold">Other ways to reach us</h2>
                    {[
                        { icon: MapPin, title: "Head Office", text: "Plot 45, Industrial Area, Kampala, Uganda" },
                        { icon: Phone, title: "Call us", text: "+256 700 000 000" },
                        { icon: Mail, title: "Email us", text: "hello@peppatarget.com" },
                    ].map((c) => (
                        <div key={c.title} className="flex gap-4 border-l-4 border-primary pl-4">
                            <c.icon className="w-5 h-5 text-primary mt-1" />
                            <div>
                                <div className="font-semibold">{c.title}</div>
                                <div className="text-sm text-muted-foreground">{c.text}</div>
                            </div>
                        </div>
                    ))}
                    <div className="aspect-[4/3] bg-surface border border-border overflow-hidden mt-6">
                        <iframe
                            title="map"
                            src="https://maps.google.com/maps?q=Kampala,Uganda&t=&z=12&ie=UTF8&iwloc=&output=embed"
                            className="w-full h-full"
                            style={{ border: 0 }}
                        />
                    </div>
                </div>
            </section>
        </div>
    );
}
